import { DownOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Dropdown, Form, Input, message, Modal } from 'antd';
import { createStyles } from 'antd-style';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getProjects,
  createProject,
  getCurrentProject,
  switchProject,
  getAISettings,
} from '@/services/ant-design-pro/api';
import { useAuthStore } from '@/store/auth';

const useStyles = createStyles(({ token }) => {
  return {
    projectSelector: {
      marginLeft: '24px',
      minWidth: '200px',
      textAlign: 'left',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    projectIcon: {
      fontSize: '16px',
      color: token.colorPrimary,
    },
    projectLabel: {
      fontSize: '12px',
      color: token.colorTextSecondary,
      marginRight: '8px',
    },
    projectItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    ownerBadge: {
      fontSize: '12px',
      padding: '0 6px',
      background: token.colorPrimaryBg,
      borderRadius: '2px',
      color: token.colorPrimary,
      marginLeft: '8px',
    },
    createProject: {
      color: token.colorPrimary,
      '&:hover': {
        opacity: 0.8,
      },
    },
  };
});

const ProjectSelector: React.FC = () => {
  const { styles } = useStyles();
  const [currentProject, setCurrentProject] = useState<API.Project>();
  const [projects, setProjects] = useState<API.Project[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [canCreateProject, setCanCreateProject] = useState(true);
  const [form] = Form.useForm();
  const { t } = useTranslation();

  const { currentUser, setCurrentProject: setGlobalCurrentProject } = useAuthStore();

  // 获取项目列表
  const fetchProjects = async () => {
    const res = await getProjects({ pageSize: 100 });
    if (res.success) {
      setProjects(res.data);
    }
  };

  // 获取当前项目
  const fetchCurrentProject = async () => {
    const res = await getCurrentProject();
    if (res.success && res.data) {
      setCurrentProject(res.data);
      // 更新全局状态
      setGlobalCurrentProject(res.data);
    }
  };

  useEffect(() => {
    fetchProjects();
    fetchCurrentProject();

    getAISettings().then((res) => {
      if (res.success && res.data?.onlyAspiceSpecialistCreateProject) {
        const isAllowed = currentUser?.isAdmin || currentUser?.isAspiceSpecialist;
        setCanCreateProject(!!isAllowed);
      }
    });
  }, []);

  const handleProjectSelect = async (project: API.Project) => {
    try {
      const res = await switchProject(Number(project.id));
      if (res.success) {
        setCurrentProject(project);
        // 更新全局状态
        setGlobalCurrentProject(project);
      }
    } catch (_error) {
      message.error(t('failed'));
    }
  };

  const handleCreateProject = async (values: {
    name: string;
    code?: string;
    description?: string;
  }) => {
    try {
      const managerId = currentUser?.id;
      if (!managerId) {
        message.error(t('getUserInfoFailed'));
        return;
      }

      const res = await createProject({
        ...values,
        managerId,
      });

      if (res.success) {
        message.success(t('addSuccess'));
        setIsModalVisible(false);
        form.resetFields();

        // 获取新创建的项目信息
        const newProject = res.data;

        // 切换到新创建的项目
        try {
          const switchRes = await switchProject(Number(newProject.id));
          if (switchRes.success) {
            setCurrentProject(newProject);
            // 更新全局状态
            setGlobalCurrentProject(newProject);
            message.success(t('saveSuccess'));

            // 跳转到项目概览页面并刷新整个页面以确保所有组件都能正确加载新项目的数据
            window.location.href = '/overview';
          }
        } catch (_switchError) {
          // message.error(t('failed'));
          // 刷新项目列表
          fetchProjects();
        }
      }
    } catch (_error) {
      // message.error(t('addFailed'));
    }
  };

  return (
    <>
      <Dropdown
        menu={{
          items: [
            ...projects.map((project) => ({
              key: project.id,
              label: (
                <div className={styles.projectItem}>
                  <span>{project.name}</span>
                  {project.managers?.some((m) => m.id === currentUser?.id) && (
                    <span className={styles.ownerBadge}>{t('projectManager')}</span>
                  )}
                </div>
              ),
              onClick: () => handleProjectSelect(project),
            })),
            ...(canCreateProject
              ? [
                  { type: 'divider' as const, key: 'divider' },
                  {
                    key: 'create',
                    label: (
                      <div className={styles.createProject}>
                        <PlusOutlined /> {t('createProject')}
                      </div>
                    ),
                    onClick: () => setIsModalVisible(true),
                  },
                ]
              : []),
          ],
        }}
        trigger={['click']}
      >
        <Button className={styles.projectSelector}>
          <span className={styles.projectLabel}>{t('currentProject')}</span>
          {currentProject?.name || t('selectProject')}
          <DownOutlined style={{ marginLeft: 8 }} />
        </Button>
      </Dropdown>

      <Modal
        title={t('createProject')}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onOk={() => form.submit()}
        destroyOnHidden
      >
        <Form form={form} onFinish={handleCreateProject} layout="vertical">
          <Form.Item
            name="name"
            label={t('projectName')}
            rules={[{ required: true, message: t('pleaseInputProjectName') }]}
          >
            <Input placeholder={t('pleaseInputProjectName')} />
          </Form.Item>
          <Form.Item name="code" label={t('projectCode')}>
            <Input placeholder={t('pleaseInputProjectCode')} />
          </Form.Item>
          <Form.Item name="description" label={t('projectDescription')}>
            <Input.TextArea placeholder={t('pleaseInputProjectDescription')} rows={4} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default ProjectSelector;
